import { ReactNode, useCallback, useMemo } from "react";
import { ScheduleTemplateListItem } from "@common/schemas/domains/accounting/scheduleTemplate.schema";
import { InjectedCommandOption } from "@components/commands/lib";
import { getBulkScheduleDirectionBlockReason } from "@components/accounting/schedules/bulk/bulkScheduleRows";
import { toTransactionBulkScheduleRow } from "@components/accounting/schedules/bulk/transactionBulkScheduleRow";
import { useBulkScheduleApply } from "@components/accounting/schedules/bulk/useBulkScheduleApply";
import { getTransactionTemplateBlockReason } from "@components/accounting/schedules/transactionScheduleTemplates";
import { useAccrualAccountTypeLookup } from "@components/accounting/schedules/useAccrualAccountType";
import { useTypedFlags } from "@hooks/useTypedFlags";
import { invalidateTransactionsQueries } from "../useTransactionsQuery";
import { TOAST_MESSAGE_KEY } from "./TransactionsBulkActionsContextProvider.lib";
import { useBulkActions } from "./useBulkActions";

interface UseBulkSetScheduleOptionsParams {
    isEnabled: boolean;
    isFieldLocked: boolean;
}

export function useBulkSetScheduleOptions({
    isEnabled,
    isFieldLocked,
}: UseBulkSetScheduleOptionsParams): {
    options: InjectedCommandOption[];
    confirmModal: ReactNode;
} {
    const bulkActions = useBulkActions();
    const { schedulesExpenses: isExpensesEnabled } = useTypedFlags();
    const selectedArray = bulkActions.selectedArray;
    const getAccrualAccountType = useAccrualAccountTypeLookup();
    const rows = useMemo(
        () =>
            selectedArray.map((transaction) =>
                toTransactionBulkScheduleRow({
                    transaction,
                    accountType: getAccrualAccountType(
                        transaction.accrualAccountId,
                    ),
                }),
            ),
        [getAccrualAccountType, selectedArray],
    );
    const templateBlockReason = useCallback(
        (template: ScheduleTemplateListItem) =>
            getTransactionTemplateBlockReason({
                template,
                isExpensesEnabled,
            }) ?? getBulkScheduleDirectionBlockReason({ rows, template }),
        [isExpensesEnabled, rows],
    );
    return useBulkScheduleApply({
        rows,
        noun: "transaction",
        isEnabled,
        isFieldLocked,
        templateBlockReason,
        toastKey: TOAST_MESSAGE_KEY,
        invalidate: invalidateTransactionsQueries,
        clearSelection: bulkActions.clearSelection,
    });
}
